/**
 * Cross-app link helper for reaching a sibling Orio360 app.
 *
 * The POS, admin and KDS apps are separate origins — different Vite dev
 * ports locally, different subdomains once hosted
 * (`pos.` / `admin.` / `kds.<domain>`). Linking to a bare `/dashboard`
 * resolves against the *current* app's origin, which has no such route.
 *
 * Vite bakes `import.meta.env.VITE_*` into the bundle at build time, so a
 * URL that depends solely on an env var is wrong for every environment the
 * build was not configured for. Deriving the sibling origin from the
 * current hostname keeps test/live correct with no build-time config.
 *
 * Resolution order:
 *  1. `VITE_{APP}_URL` env override (explicit escape hatch).
 *  2. Hosted: swap the leading app label on the current hostname.
 *  3. Local dev: swap the Vite dev port on the current origin.
 *  4. Last resort: return the relative path unchanged (preserves prior
 *     behaviour rather than throwing).
 */
export type AppName = 'pos' | 'admin' | 'kds';

const DEV_PORT: Record<AppName, string> = {
  pos: '3001',
  admin: '3002',
  kds: '3003',
};

const ENV_KEY: Record<AppName, string> = {
  pos: 'VITE_POS_URL',
  admin: 'VITE_ADMIN_URL',
  kds: 'VITE_KDS_URL',
};

export function getAppBaseUrl(app: AppName): string | null {
  const configured = (import.meta.env as Record<string, string | undefined>)[
    ENV_KEY[app]
  ];
  if (configured && configured.trim().length > 0) {
    return configured.trim().replace(/\/+$/, '');
  }

  if (typeof window !== 'undefined' && window.location) {
    const { protocol, hostname, port } = window.location;

    // Hosted — pos.bbpos.example.in -> admin.bbpos.example.in.
    // Only fires when the leading label is a known app name, so an
    // unrecognized host is never guessed at.
    const [first, ...rest] = hostname.split('.');
    if (rest.length > 0 && first in DEV_PORT) {
      const suffix = port ? `:${port}` : '';
      return `${protocol}//${[app, ...rest].join('.')}${suffix}`;
    }

    // Local dev — swap the Vite port on the current origin.
    if (port && Object.values(DEV_PORT).includes(port)) {
      return `${protocol}//${hostname}:${DEV_PORT[app]}`;
    }
  }

  return null;
}

/**
 * Build an absolute URL into a sibling app for a given path
 * (e.g. `/dashboard`). Falls back to the relative path when no origin
 * can be resolved.
 */
export function appUrl(app: AppName, path: string): string {
  const base = getAppBaseUrl(app);
  const normalized = path.startsWith('/') ? path : `/${path}`;
  return base ? `${base}${normalized}` : normalized;
}
